import * as React from "react";
import { useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { REPORT_TZ_FALLBACK, DEFAULT_ORIENTATION } from "report/Constants";
import AutoRefreshDisabledProvider from "report/common/AutoRefreshDisabledProvider";
import UserDateTimeProvider from "contexts/UserDateTimeProvider";
import ReportParameters from "report/report-render-page/ReportParameters";
import ReportWidgets from "report/report-render-page/ReportWidgets";
import GlobalPDFStyles from "search/ExportWidgetAction/pdf-export-widget-renderer/GlobalPDFStyles";
import { REPORT_PREVIEW_ID } from "report/report-creation/ReportPreview";
import CoverPage from "report/common/CoverPage";
import Toc from "report/common/Toc";
import usePaperDimensions from "report/report-creation/common/usePaperDimensions";
const InvisibleDiv = styled.div`
  display: none;
`;
const useUpdateRenderComplete = (widgets = []) => {
  var _a;
  const [renderedWidgets, setRenderedWidgets] = useState([]);
  const onWidgetRenderComplete = useCallback(
    (widgetId) => () => {
      if (!renderedWidgets.includes(widgetId)) {
        setRenderedWidgets((prevWidgets) => [...prevWidgets, widgetId]);
      }
    },
    [renderedWidgets]
  );
  const progress = [renderedWidgets.length, (_a = widgets == null ? void 0 : widgets.length) != null ? _a : 0];
  return {
    renderComplete: (widgets == null ? void 0 : widgets.length) === 0 || widgets.length === renderedWidgets.length,
    onWidgetRenderComplete,
    progress
  };
};
const Page = styled.div(
  ({ $height }) => css`
    break-after: always;
    ${$height ? `min-height: ${$height}px;` : ""}
  `
);
const RenderedReport = ({
  report,
  preview = false,
  width = void 0,
  handleTocClick = void 0,
  widgetResultProvider
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const reportTz = report.timezone || REPORT_TZ_FALLBACK;
  const { onWidgetRenderComplete, renderComplete, progress } = useUpdateRenderComplete(report == null ? void 0 : report.widgets);
  const paperDimensions = usePaperDimensions((_a = report.layout) == null ? void 0 : _a.pageSize, (_b = report.layout) == null ? void 0 : _b.orientation);
  const previewHeight = preview ? paperDimensions.height : void 0;
  return /* @__PURE__ */ React.createElement(AutoRefreshDisabledProvider, null, /* @__PURE__ */ React.createElement(UserDateTimeProvider, { tz: reportTz }, /* @__PURE__ */ React.createElement(
    GlobalPDFStyles,
    {
      $orientation: (_d = (_c = report.layout) == null ? void 0 : _c.orientation) != null ? _d : DEFAULT_ORIENTATION,
      $rootSelector: preview ? `#${REPORT_PREVIEW_ID}` : void 0,
      $printHeader: (_e = report.layout) == null ? void 0 : _e.printHeader,
      $printFooter: (_f = report.layout) == null ? void 0 : _f.printFooter
    }
  ), /* @__PURE__ */ React.createElement(Page, { $height: previewHeight }, /* @__PURE__ */ React.createElement(CoverPage, { title: report.title, subtitle: report.subtitle, description: report.description, tz: reportTz })), ((_g = report.layout) == null ? void 0 : _g.printToc) && report.widgets.length > 0 && /* @__PURE__ */ React.createElement(Page, { $height: previewHeight }, /* @__PURE__ */ React.createElement(Toc, { widgets: report.widgets, handleTocClick })), report.widgets.length > 0 && /* @__PURE__ */ React.createElement(React.Fragment, null, report.parameterValues && /* @__PURE__ */ React.createElement(ReportParameters, { parameters: report.parameters, parameterValues: report.parameterValues }), /* @__PURE__ */ React.createElement(
    ReportWidgets,
    {
      widgets: report.widgets,
      width,
      widgetResultProvider,
      positions: report.positions,
      orientation: (_i = (_h = report.layout) == null ? void 0 : _h.orientation) != null ? _i : DEFAULT_ORIENTATION,
      hideWidgetQuery: report.hideWidgetQuery,
      hideWidgetDescription: report.hideWidgetDescription,
      onWidgetRenderComplete
    }
  )), /* @__PURE__ */ React.createElement(InvisibleDiv, { id: "render-started", "data-rendered-widgets": progress[0], "data-total-widgets": progress[1] }), renderComplete && /* @__PURE__ */ React.createElement(InvisibleDiv, { id: "render-complete" })));
};
export default RenderedReport;
